import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { UserPlus, CheckCircle2, XCircle, Trash2, Shield, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagementPage = () => {
  const { t, language } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tab, setTab] = useState('all'); // all, pending

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff'
  });

  const roleLabels = {
    super_admin: 'Süper Yönetici',
    company_admin: 'Şirket Yöneticisi',
    finance: 'Finans',
    operations: 'Operasyon',
    maintenance: 'Bakım',
    staff: 'Personel',
    tenant: 'Kiracı'
  };

  const roleColors = {
    super_admin: 'bg-black text-white',
    company_admin: 'bg-gray-800 text-white',
    finance: 'bg-blue-100 text-blue-800 border-blue-300',
    operations: 'bg-green-100 text-green-800 border-green-300',
    maintenance: 'bg-orange-100 text-orange-800 border-orange-300',
    staff: 'bg-gray-100 text-gray-800 border-gray-300',
    tenant: 'bg-purple-100 text-purple-800 border-purple-300'
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/users`, formData, { withCredentials: true });
      toast.success(language === 'tr' ? 'Kullanıcı oluşturuldu' : 'User created');
      setIsModalOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'staff' });
      fetchUsers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/approve`, {}, { withCredentials: true });
      toast.success(language === 'tr' ? 'Kullanıcı onaylandı' : 'User approved');
      fetchUsers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm(language === 'tr' ? 'Bu kullanıcıyı silmek istediğinize emin misiniz?' : 'Are you sure you want to delete this user?')) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/reject`, {}, { withCredentials: true });
      toast.success(language === 'tr' ? 'Kullanıcı reddedildi' : 'User rejected');
      fetchUsers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchUsers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const pendingUsers = users.filter(u => u.approved === false);
  const filteredUsers = tab === 'pending' ? pendingUsers : users;

  const availableRoles = currentUser?.role === 'super_admin' 
    ? ['company_admin', 'finance', 'operations', 'maintenance', 'staff', 'tenant']
    : ['finance', 'operations', 'maintenance', 'staff', 'tenant'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('userManagement')}</h1>
          <p className="text-gray-500 text-sm mt-1">
            {users.length} {language === 'tr' ? 'kullanıcı' : 'users'}
            {pendingUsers.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 text-xs font-bold">
                {pendingUsers.length} {t('pendingApproval')}
              </span>
            )}
          </p>
        </div>
        <Button 
          onClick={() => setIsModalOpen(true)}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          {t('addUser')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setTab('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'all' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-all-users"
        >
          {language === 'tr' ? 'Tüm Kullanıcılar' : 'All Users'} ({users.length})
        </button>
        <button
          onClick={() => setTab('pending')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === 'pending' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-pending-users"
        >
          <Clock className="w-3.5 h-3.5" />
          {t('pendingApproval')} ({pendingUsers.length})
        </button>
      </div>

      {/* Users Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('name')}</th>
              <th>{t('email')}</th>
              <th>{language === 'tr' ? 'Rol' : 'Role'}</th>
              <th>{t('status')}</th>
              <th>{language === 'tr' ? 'Kayıt Tarihi' : 'Registered'}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 flex items-center justify-center text-xs font-bold">
                        {u.name?.charAt(0)?.toUpperCase()}
                      </div>
                      {u.name}
                    </div>
                  </td>
                  <td className="text-sm text-gray-600">{u.email}</td>
                  <td>
                    <span className={`px-2 py-0.5 text-xs font-bold uppercase border ${roleColors[u.role] || 'bg-gray-100'}`}>
                      {roleLabels[u.role] || u.role}
                    </span>
                  </td>
                  <td>
                    {u.approved === false ? (
                      <span className="px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-700 border border-amber-300 flex items-center gap-1 w-fit">
                        <Clock className="w-3 h-3" />
                        {t('pendingApproval')}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-300 flex items-center gap-1 w-fit">
                        <CheckCircle2 className="w-3 h-3" />
                        {t('approved')}
                      </span>
                    )}
                  </td>
                  <td className="text-sm text-gray-500">{u.created_at?.split('T')[0]}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      {u.approved === false && (
                        <>
                          <button
                            onClick={() => handleApprove(u.id)}
                            className="p-1 hover:bg-emerald-50 text-emerald-600"
                            title={t('approve')}
                            data-testid={`approve-user-${u.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(u.id)}
                            className="p-1 hover:bg-red-50 text-red-600"
                            title={t('reject')}
                            data-testid={`reject-user-${u.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {u.id !== currentUser?.id && u.role !== 'super_admin' && (
                        <button
                          onClick={() => handleDelete(u.id)}
                          className="p-1 hover:bg-red-50 text-red-600"
                          title={t('delete')}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-8 text-gray-500">
                  {t('noUsers')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {t('createUser')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('name')} *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                className="rounded-none mt-1"
                data-testid="create-user-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('email')} *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                className="rounded-none mt-1"
                data-testid="create-user-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{language === 'tr' ? 'Şifre' : 'Password'} *</label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                className="rounded-none mt-1"
                data-testid="create-user-password"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{language === 'tr' ? 'Rol' : 'Role'} *</label>
              <Select 
                value={formData.role} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, role: val }))}
              >
                <SelectTrigger className="rounded-none mt-1" data-testid="create-user-role">
                  <SelectValue placeholder={t('selectRole')} />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role} value={role}>{roleLabels[role]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="create-user-submit">
                {t('createUser')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
